import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import { LoadingSpinner } from '../ui/index.js';
import type { Location, LocationSupport, Pin } from '../../types/index.js';

export function LocationInfoPanel() {
  const { activeLocationId, setActiveLocation, detailPin, setDetailPin } = useMapStore();

  if (activeLocationId) {
    return <LibraryLocationDetail locationId={activeLocationId} onClose={() => setActiveLocation(null)} />;
  }
  if (detailPin) {
    return <PinDetail pin={detailPin} onClose={() => setDetailPin(null)} />;
  }
  return null;
}

// ─── Shared auto-save notes hook ──────────────────────────────────────────────
function useAutoSaveNotes(initial: string, save: (text: string) => void) {
  const [notes, setNotes] = useState(initial);
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onChange = (text: string) => {
    setNotes(text);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      await save(text);
      setSaving(false);
    }, 800);
  };

  return { notes, saving, onChange, setNotes };
}

// ─── Library location detail ──────────────────────────────────────────────────
function LibraryLocationDetail({ locationId, onClose }: { locationId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [sharing, setSharing] = useState(false);

  const { data: allLocations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const result = await api.get<{ locations: Location[] }>('/locations');
      return result.success ? result.data.locations : [];
    },
  });

  const { data: supports = [], isLoading: supLoading } = useQuery({
    queryKey: ['location-supports', locationId],
    queryFn: async () => {
      const result = await api.get<{ supports: LocationSupport[] }>(`/locations/${locationId}/supports`);
      return result.success ? result.data.supports : [];
    },
  });

  const location = allLocations.find(l => l.id === locationId);

  const handleShare = async () => {
    setSharing(true);
    try {
      const result = await api.post<{ token: string; url: string }>(`/locations/${locationId}/share`, {});
      if (!result.success) { toast.error('Failed to create share link'); return; }
      const url = window.location.origin + result.data.url;
      await navigator.clipboard.writeText(url);
      toast.success('Location link copied to clipboard!');
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const saveNotes = async (text: string) => {
    await api.put(`/locations/${locationId}`, { notes: text });
    qc.invalidateQueries({ queryKey: ['locations'] });
  };

  const notesHook = useAutoSaveNotes(location?.notes ?? '', saveNotes);

  // Sync notes textarea when location data first loads or ID changes
  useEffect(() => {
    if (location) notesHook.setNotes(location.notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const photos: string[] = (() => {
    try { return JSON.parse(location?.photosJson ?? '[]'); } catch { return []; }
  })();

  if (!location) {
    return (
      <PanelShell onClose={onClose}>
        <div className="flex justify-center py-8"><LoadingSpinner size="sm" /></div>
      </PanelShell>
    );
  }

  return (
    <PanelShell onClose={onClose}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 shrink-0 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{location.businessName || location.name}</h3>
          {location.businessName && <p className="text-xs text-gray-500 truncate">{location.name}</p>}
          <p className="text-xs text-gray-400 mt-0.5 truncate">📍 {location.address}</p>
          {location.siteContactName && (
            <p className="text-xs font-medium text-brand-700 mt-1 truncate">👤 {location.siteContactName}</p>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={handleShare} title={sharing ? 'Copying…' : 'Share location'} className="text-gray-400 hover:text-brand-500 text-sm leading-none p-0.5">🔗</button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none mt-0.5">×</button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
        {/* Support locations */}
        <div className="px-4 py-3 space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Support Locations</p>
          {supLoading ? (
            <div className="flex justify-center py-2"><LoadingSpinner size="sm" /></div>
          ) : supports.length === 0 ? (
            <p className="text-xs text-gray-400">None added yet</p>
          ) : (
            supports.map(sup => (
              <div key={sup.id} className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{sup.name}</p>
                  <p className="text-xs text-gray-400 truncate">{sup.address}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Site Contact */}
        {(location.siteContactName || location.siteContactPhone || location.siteContactEmail) && (
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Site Contact</p>
            {location.siteContactName && <p className="text-sm font-medium text-gray-800">{location.siteContactName}</p>}
            <div className="flex flex-wrap gap-x-3 mt-0.5">
              {location.siteContactPhone && (
                <a href={`tel:${location.siteContactPhone}`} className="text-xs text-brand-600 hover:underline">{location.siteContactPhone}</a>
              )}
              {location.siteContactEmail && (
                <a href={`mailto:${location.siteContactEmail}`} className="text-xs text-brand-600 hover:underline">{location.siteContactEmail}</a>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Notes</p>
            {notesHook.saving && <span className="text-xs text-gray-400">Saving…</span>}
          </div>
          <textarea
            value={notesHook.notes}
            onChange={e => notesHook.onChange(e.target.value)}
            placeholder="Add notes about this location…"
            rows={4}
            className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-400 resize-none"
          />
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Photos</p>
            <div className="grid grid-cols-3 gap-1.5">
              {photos.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(src, '_blank')}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </PanelShell>
  );
}

// ─── Pin detail ───────────────────────────────────────────────────────────────
const PIN_EMOJI: Record<string, string> = {
  main: '📍', basecamp: '🏠', parking: '🅿️', hospital: '🏥',
};

function PinDetail({ pin, onClose }: { pin: Pin; onClose: () => void }) {
  const qc = useQueryClient();

  const saveNotes = async (text: string) => {
    await api.put(`/pins/${pin.id}`, { notes: text });
    qc.invalidateQueries({ queryKey: ['pins', pin.sceneId] });
  };

  const notesHook = useAutoSaveNotes(pin.notes, saveNotes);

  // Reset when a different pin is shown
  useEffect(() => {
    notesHook.setNotes(pin.notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin.id]);

  const photos: string[] = (() => {
    try { return JSON.parse(pin.photosJson ?? '[]'); } catch { return []; }
  })();

  return (
    <PanelShell onClose={onClose}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 shrink-0 flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-base">{PIN_EMOJI[pin.pinType] ?? '📍'}</span>
            <h3 className="font-semibold text-gray-900 truncate">{pin.title}</h3>
          </div>
          {pin.address && <p className="text-xs text-gray-500 mt-0.5 truncate">{pin.address}</p>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none shrink-0 mt-0.5">×</button>
      </div>

      {/* Notes */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Notes</p>
          {notesHook.saving && <span className="text-xs text-gray-400">Saving…</span>}
        </div>
        <textarea
          value={notesHook.notes}
          onChange={e => notesHook.onChange(e.target.value)}
          placeholder="Add notes about this location…"
          rows={4}
          className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-400 resize-none"
        />
      </div>

      {/* Photos (if any exist on pin) */}
      {photos.length > 0 && (
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Photos</p>
          <div className="grid grid-cols-3 gap-1.5">
            {photos.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open(src, '_blank')}
              />
            ))}
          </div>
        </div>
      )}
    </PanelShell>
  );
}

// ─── Shared panel shell ───────────────────────────────────────────────────────
function PanelShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  // Stop map clicks from propagating through the panel
  return (
    <div
      className="absolute top-4 right-4 z-30 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 flex flex-col"
      style={{ maxHeight: 'calc(50vh - 1rem)' }}
      onClick={e => e.stopPropagation()}
    >
      {children}
      {/* Close button fallback if header doesn't include one — children usually render their own */}
      {!children && (
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl">×</button>
      )}
    </div>
  );
}
